<script setup lang="ts">
// Renders the `ui-spotlight` request (useSpotlight): the page dims except for
// a pulsing cut-out around `[data-spot=target]`, and a bubble with a tail
// points at it — beside a sidebar item, below/above anything else. The bubble
// (and the ring itself) OPENS the target, so „ukázat kde" ends one click from
// the actual screen instead of in a bare „vlevo v menu" message. A sidebar
// item the web has switched off (e.g. „Zprávy" without a contact form) has
// no element to ring — then the bubble offers the page's route directly.
// Dismisses on backdrop click, Esc or after a while; re-measures on
// resize/scroll. Mounted once in SidebarLayout so every page shares it.
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useSpotlight } from '~/composables/useSpotlight'

const { req, clear } = useSpotlight()
const router = useRouter()
const drawerOpen = useState('wb-drawer-open', () => false)

// Fallback routes for sidebar anchors whose menu item may be hidden by a
// per-web feature switch (ids = SidebarNavigation item ids).
const SIDEBAR_ROUTES: Record<string, string> = {
  domains: '/domains/',
  contact: '/contact/',
  business: '/firma/',
  seo: '/seo/',
  statistics: '/statistics/',
  media: '/media/',
}

interface Box {
  top: number
  left: number
  width: number
  height: number
}
type Placement = 'right' | 'below' | 'above' | 'free'

const box = ref<Box | null>(null)
const hint = ref('')
const visible = ref(false)
const fallbackRoute = ref<string | null>(null)
const clickable = ref(false)
let hideTimer: ReturnType<typeof setTimeout> | null = null
let target: HTMLElement | null = null
let openedDrawer = false

const BUBBLE_W = 300

function measure() {
  if (!target || !target.isConnected) {
    box.value = null
    return
  }
  const r = target.getBoundingClientRect()
  if (r.width === 0 && r.height === 0) {
    box.value = null
    return
  }
  const pad = 6
  box.value = { top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 }
}

function stopTimer() {
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = null
}
function startTimer() {
  stopTimer()
  hideTimer = setTimeout(() => dismiss(), 15000)
}

function dismiss(keepDrawer = false) {
  visible.value = false
  target = null
  box.value = null
  fallbackRoute.value = null
  stopTimer()
  // We opened the phone drawer only to point into it — close it again unless
  // the user is about to navigate from it.
  if (openedDrawer && !keepDrawer) drawerOpen.value = false
  openedDrawer = false
  clear()
}

function open() {
  const t = target
  const route = fallbackRoute.value
  dismiss(true)
  if (t && clickable.value) t.click()
  else if (route) router.push(route)
}

function isClickable(el: HTMLElement | null) {
  if (!el) return false
  if (!el.matches('button, a[href], [role="button"]')) return false
  return !(el as HTMLButtonElement).disabled
}

async function show(t: string, h: string) {
  openedDrawer = false
  target = document.querySelector<HTMLElement>(`[data-spot="${t}"]`)
  // Sidebar items live in the drawer on phones — open it so the ring has
  // something to point at.
  if (!target && t.startsWith('sidebar:') && !drawerOpen.value) {
    drawerOpen.value = true
    openedDrawer = true
    await nextTick()
    await new Promise((r) => setTimeout(r, 250))
    target = document.querySelector<HTMLElement>(`[data-spot="${t}"]`)
    if (!target) {
      // The item isn't in the menu at all — don't leave an empty drawer open.
      drawerOpen.value = false
      openedDrawer = false
    }
  }
  clickable.value = isClickable(target)
  fallbackRoute.value = !target && t.startsWith('sidebar:') ? SIDEBAR_ROUTES[t.slice(8)] || null : null
  if (target) target.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  hint.value = h
  visible.value = true
  await nextTick()
  followLayout()
  startTimer()
}

// The phone drawer slides in and scrollIntoView may smooth-scroll: re-measure
// every frame for a moment so the ring/bubble land where the item ENDS UP,
// not where it was mid-animation.
let followUntil = 0
function followLayout() {
  followUntil = performance.now() + 700
  const tick = () => {
    if (!visible.value) return
    measure()
    if (performance.now() < followUntil) requestAnimationFrame(tick)
  }
  tick()
}

watch(req, (r) => {
  if (r) show(r.target, r.hint)
})

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && visible.value) dismiss()
}
function onScroll() {
  if (visible.value) measure()
}
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', onKey)
  window.addEventListener('resize', onScroll)
  window.addEventListener('scroll', onScroll, true)
}
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('resize', onScroll)
  window.removeEventListener('scroll', onScroll, true)
  stopTimer()
})

// With the ring on screen the hint's „Menu vlevo → …" prefix only repeats
// what the ring already shows — keep the useful rest („Zprávy → …").
const text = computed(() => {
  const h = hint.value || ''
  if (!box.value) return h || 'Tuhle část teď na obrazovce nevidím.'
  return h.replace(/^Menu vlevo\s*→\s*/i, '') || 'Tady.'
})
const kicker = computed(() => (box.value ? 'Tady to najdete' : 'Kde to najdete'))
const openLabel = computed(() => (box.value ? 'Otevřít' : 'Přejít tam'))
const canOpen = computed(() => (box.value ? clickable.value : !!fallbackRoute.value))

const placement = computed<Placement>(() => {
  const b = box.value
  if (!b || typeof window === 'undefined') return 'free'
  const vw = window.innerWidth
  // A narrow item on the left edge (desktop rail): bubble to its right. Wide
  // targets (phone drawer rows) get it below/above instead.
  if (b.width < 160 && b.left < vw / 3 && b.left + b.width + 18 + Math.min(BUBBLE_W, vw - 24) < vw) return 'right'
  return b.top + b.height + 150 < window.innerHeight ? 'below' : 'above'
})

function bubbleStyle(): Record<string, string> {
  const b = box.value
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  const w = Math.min(BUBBLE_W, vw - 24)
  if (!b) return { left: '50%', bottom: '28px', transform: 'translateX(-50%)', width: `${w}px` }
  if (placement.value === 'right') {
    const top = Math.min(Math.max(b.top + b.height / 2, 60), vh - 60)
    return { left: `${b.left + b.width + 16}px`, top: `${top}px`, transform: 'translateY(-50%)', width: `${w}px` }
  }
  const center = b.left + b.width / 2
  const left = Math.min(Math.max(center - w / 2, 12), vw - w - 12)
  // Tail follows the ring even when the bubble is clamped to the viewport.
  const tail = `${Math.min(Math.max(center - left, 18), w - 18)}px`
  return placement.value === 'below'
    ? { left: `${left}px`, top: `${b.top + b.height + 14}px`, width: `${w}px`, '--tail-x': tail }
    : { left: `${left}px`, top: `${b.top - 14}px`, transform: 'translateY(-100%)', width: `${w}px`, '--tail-x': tail }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="spot" :class="{ 'spot--noring': !box }" @click="dismiss()">
      <button
        v-if="box"
        type="button"
        class="spot__ring"
        :class="{ 'spot__ring--open': clickable }"
        :style="{ top: box.top + 'px', left: box.left + 'px', width: box.width + 'px', height: box.height + 'px' }"
        :aria-label="clickable ? openLabel : 'Zvýrazněné místo'"
        @click.stop="clickable ? open() : dismiss()"
      />
      <div
        class="spot__bubble"
        :class="'spot__bubble--' + placement"
        :style="bubbleStyle()"
        role="dialog"
        aria-live="polite"
        @click.stop
        @mouseenter="stopTimer"
        @mouseleave="startTimer"
      >
        <div class="spot__kicker">{{ kicker }}</div>
        <div class="spot__text">{{ text }}</div>
        <div class="spot__actions">
          <button v-if="canOpen" type="button" class="spot__btn spot__btn--primary" @click="open">
            {{ openLabel }}
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
          <button type="button" class="spot__btn" @click="dismiss()">{{ canOpen ? 'Zavřít' : 'Rozumím' }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.spot {
  position: fixed;
  inset: 0;
  z-index: 4000;
  pointer-events: auto;
}
/* No element to ring: a light veil so the bubble reads as the focus. */
.spot--noring {
  background: color-mix(in srgb, #0b1020 18%, transparent);
}
.spot__ring {
  position: fixed;
  padding: 0;
  border-radius: 12px;
  border: 2px solid var(--accent, #2b3a8f);
  background: transparent;
  /* The huge spread shadow is the dimmed page — everything but the target. */
  box-shadow:
    0 0 0 4px color-mix(in srgb, var(--accent, #2b3a8f) 25%, transparent),
    0 0 0 9999px color-mix(in srgb, #0b1020 32%, transparent);
  cursor: default;
  animation: spot-pulse 1.4s ease-in-out infinite;
}
.spot__ring--open {
  cursor: pointer;
}
@keyframes spot-pulse {
  0%,
  100% {
    box-shadow:
      0 0 0 4px color-mix(in srgb, var(--accent, #2b3a8f) 25%, transparent),
      0 0 0 9999px color-mix(in srgb, #0b1020 32%, transparent);
  }
  50% {
    box-shadow:
      0 0 0 10px color-mix(in srgb, var(--accent, #2b3a8f) 10%, transparent),
      0 0 0 9999px color-mix(in srgb, #0b1020 32%, transparent);
  }
}

.spot__bubble {
  position: fixed;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--card, #fff);
  color: var(--ink, #14161f);
  border: 1px solid var(--line, #e4e4ea);
  box-shadow: 0 14px 36px rgba(10, 14, 40, 0.22);
  font-size: 13.5px;
  line-height: 1.4;
}
/* Tail pointing at the ring + a small nudge toward it, so the eye follows. */
.spot__bubble::before {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
  background: inherit;
  border: inherit;
  transform: rotate(45deg);
}
.spot__bubble--right::before {
  left: -7px;
  top: calc(50% - 6px);
  border-top-color: transparent;
  border-right-color: transparent;
}
.spot__bubble--below::before {
  top: -7px;
  left: calc(var(--tail-x, 50%) - 6px);
  border-right-color: transparent;
  border-bottom-color: transparent;
}
.spot__bubble--above::before {
  bottom: -7px;
  left: calc(var(--tail-x, 50%) - 6px);
  border-left-color: transparent;
  border-top-color: transparent;
}
.spot__bubble--free::before {
  display: none;
}
.spot__bubble--right {
  animation: spot-nudge-x 1.4s ease-in-out infinite;
}
.spot__bubble--below {
  animation: spot-nudge-up 1.4s ease-in-out infinite;
}
@keyframes spot-nudge-x {
  0%,
  100% {
    margin-left: 0;
  }
  50% {
    margin-left: -5px;
  }
}
@keyframes spot-nudge-up {
  0%,
  100% {
    margin-top: 0;
  }
  50% {
    margin-top: -4px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .spot__ring,
  .spot__bubble {
    animation: none;
  }
}
.spot__kicker {
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent, #2b3a8f);
  margin-bottom: 3px;
}
.spot__text {
  overflow-wrap: anywhere;
}
.spot__actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
.spot__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--line, #e4e4ea);
  border-radius: 8px;
  padding: 6px 12px;
  background: transparent;
  color: var(--ink-2, #3a3f52);
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.spot__btn--primary {
  border-color: var(--accent, #2b3a8f);
  background: var(--accent, #2b3a8f);
  color: #fff;
}
</style>
